import React, { useState, useEffect, useRef } from "react";
import Icon from "@mdi/react";
import {
    mdiChevronDown,
    mdiChevronRight,
    mdiInformationOutline,
    mdiPaw,
    mdiAccountCircleOutline,
    mdiAccountPlusOutline,
    mdiCreditCardCheck,
    mdiCreditCardClockOutline,
    mdiCreditCardSyncOutline,
    mdiCreditCardPlusOutline,
    mdiTrashCan,
} from "@mdi/js";
import "./ClassesHelpModal.css";
import "./ClassSettingsHelpModal.css";

// Renders a real status/payment pill swatch matching the live card styles in
// ClassStudentsPage.css, so the help legend shows the actual pills (not just
// colour words). `variant` picks fill vs border treatment.
// Fixed-width left cell so every pill sits in the same column and all the
// descriptions line up in a clean right column (ragged left edge reads badly).
const HelpPillRow = ({ label, fill, color, border, children }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <span style={{ flex: "0 0 160px", display: "flex", justifyContent: "flex-start" }}>
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    borderRadius: "12px",
                    padding: "3px 10px",
                    whiteSpace: "nowrap",
                    background: fill,
                    color,
                    border: border ? `1px solid ${color}` : "none",
                }}>
                {label}
            </span>
        </span>
        <span style={{ flex: "1 1 auto" }}>{children}</span>
    </div>
);

const ClassStudentsHelpModal = ({ isOpen, onClose }) => {
    const [expandedSections, setExpandedSections] = useState([]);
    const sectionRefs = useRef({});

    useEffect(() => {
        if (!isOpen) setExpandedSections([]);
    }, [isOpen]);

    const toggleSection = sectionId => {
        const isExpanding = !expandedSections.includes(sectionId);
        setExpandedSections(prev =>
            prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
        );
        if (isExpanding) {
            setTimeout(() => {
                sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 0);
        }
    };

    if (!isOpen) return null;

    const sections = [
        {
            id: "overview",
            title: "1. Overview",
            content: (
                <>
                    <p>
                        The Students page is your central hub for everyone connected to your school. It is divided
                        into five tabs across the top:
                    </p>
                    <ul style={{ marginTop: "8px" }}>
                        <li><strong>Prospects</strong>: new enquiries, class upgrade requests, and trial deal buyers</li>
                        <li><strong>Students</strong>: your full student roster with payment and profile management</li>
                        <li><strong>Attendance</strong>: attendance reports by term or class</li>
                        <li><strong>History</strong>: past and cancelled enrolments across your school</li>
                        <li><strong>Payments</strong>: a per-student payment history and balance ledger across every term, with square-up actions</li>
                        <li><strong>Print Class List</strong>: generate a printable roll-call sheet</li>
                    </ul>
                    <p style={{ marginTop: "10px" }}>
                        Each tab is described in detail in the sections below.
                    </p>
                </>
            ),
        },
        {
            id: "prospects-tab",
            title: "2. Prospects tab",
            content: (
                <>
                    <p>
                        The Prospects tab is a unified pipeline that brings together three types of incoming activity:
                        enquiries, upgrade requests, and trial deal buyers.
                    </p>

                    <p style={{ marginTop: "12px" }}><strong>Enquiries</strong></p>
                    <p>
                        When a prospective student submits the sign-up form on your public school page, they appear
                        here as an enquiry. You can enrol them directly into the relevant class or dismiss the
                        enquiry if it is not relevant.
                    </p>

                    <p style={{ marginTop: "12px" }}><strong>Upgrade requests</strong></p>
                    <p>
                        When an enrolled student requests to move between classes (for example, from Beginners to
                        Intermediate), a request card appears here. You can approve or decline it from the card.
                    </p>

                    <p style={{ marginTop: "12px" }}><strong>Trial deal buyers (prospect pipeline)</strong></p>
                    <p>
                        When a brand-new student purchases one of your trial deals on your public page, they are
                        automatically added to the prospect pipeline. Each prospect card shows a source pill
                        (Trial, Walk-in, Enquiry, etc.) indicating how they came to your school.
                    </p>
                    <p style={{ marginTop: "8px" }}>
                        Filter the pipeline by status:
                    </p>
                    <ul>
                        <li>
                            <strong>Active.</strong> Prospects who have not yet enrolled in a full class. Focus
                            your follow-up here.
                        </li>
                        <li>
                            <strong>Converted.</strong> Prospects who went on to enrol in a full class. Enrolment
                            converts them automatically.
                        </li>
                        <li>
                            <strong>Lost.</strong> Prospects you have manually marked as not proceeding.
                        </li>
                    </ul>
                    <p style={{ marginTop: "10px" }}>
                        Click <strong>View</strong> on any prospect card to see their contact details and which
                        trial deal they purchased. If a student arranges enrolment outside the normal flow (for
                        example, a cash payment), use <strong>Mark Converted</strong> to move them off the Active
                        list manually.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        <strong>Note:</strong> Only brand-new students enter the prospect pipeline. If a student
                        already has a confirmed enrolment at your school and buys a trial deal, they are not added
                        here.
                    </p>
                </>
            ),
        },
        {
            id: "students-tab",
            title: "3. Students tab",
            content: (
                <>
                    <p>
                        The Students tab shows every student profile attached to your school. You can search by
                        name and use the status filters to surface specific groups.
                    </p>

                    <p style={{ marginTop: "12px" }}><strong>Status filters</strong></p>
                    <ul>
                        <li>
                            <strong>All.</strong> Every student in your roster.
                        </li>
                        <li>
                            <strong>Not Enrolled.</strong> Students who were confirmed in a recently ended term but
                            have no active enrolment in the current term. Use this for retention follow-up after a
                            term rolls over.
                        </li>
                        <li>
                            <strong>On Break.</strong> Students you have placed on a break. They are excluded from
                            check-in rosters and bulk email actions. Open their profile and click{" "}
                            <strong>Reactivate</strong> to bring them back.
                        </li>
                    </ul>

                    <p style={{ marginTop: "14px" }}><strong>Status pills</strong></p>
                    <p style={{ marginTop: "4px" }}>
                        Each enrolment on a student card shows the class, time, and term on the left, with its status
                        pills lined up in their own column on the right for quick scanning. The pills are an enrolment
                        pill plus a payment pill where a payment relationship exists. Enrolment pills have a solid tint
                        fill; payment pills have a thin coloured border so the two read as distinct families.
                    </p>
                    <p style={{ marginTop: "8px", marginBottom: "6px", fontWeight: 600 }}>Enrolment status pills</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <HelpPillRow label="Enrolled" fill="var(--dl-color-green-100)" color="var(--dl-color-green-700)">
                            Actively enrolled in the class or short course.
                        </HelpPillRow>
                        <HelpPillRow label="Unconfirmed" fill="var(--dl-color-yellow-100)" color="var(--dl-color-orange-default)">
                            Rolled over from a previous term and not yet re-confirmed. Confirms automatically at their
                            first check-in.
                        </HelpPillRow>
                        <HelpPillRow label="On hold" fill="var(--dl-color-orange-50)" color="var(--dl-color-orange-default)">
                            A role-restricted short course enrolment awaiting your approval. No payment pill shows until
                            you approve it.
                        </HelpPillRow>
                    </div>

                    <p style={{ marginTop: "14px", marginBottom: "6px", fontWeight: 600 }}>
                        Payment status pills
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <HelpPillRow label="Paid in full" fill="var(--dl-color-green-100)" color="var(--dl-color-greentick)" border>
                            The enrolment is fully paid and settled.
                        </HelpPillRow>
                        <HelpPillRow label="Paid - unreconciled" fill="var(--dl-color-green-100)" color="var(--dl-color-greentick)" border>
                            Paid outside the platform (cash, EFTPOS or bank transfer). It counts as paid. Reconcile it in
                            Manage Finances against your bank/EFTPOS transactions etc. This can be done whenever you
                            balance your books.
                        </HelpPillRow>
                        <HelpPillRow label="1/3 paid" fill="var(--dl-color-purple-100)" color="var(--dl-color-purple-900)" border>
                            Instalment plan progress: instalments paid so far out of the total.
                        </HelpPillRow>
                        <HelpPillRow label="Awaiting price package" fill="var(--dl-color-blue-50)" color="var(--dl-color-blue-default)" border>
                            You have not created a price package for this enrolment yet. The next step is yours.
                        </HelpPillRow>
                        <HelpPillRow label="Awaiting payment" fill="var(--dl-color-gray-white)" color="var(--dl-color-needs-action)" border>
                            A price package has been issued and the student has not paid yet. The next step is theirs,
                            so chase the money.
                        </HelpPillRow>
                    </div>

                    <p style={{ marginTop: "14px", marginBottom: "6px", fontWeight: 600 }}>In the student profile</p>
                    <p style={{ marginTop: 0 }}>
                        <strong>Rolled over, term not open yet</strong> (yellow). Shown inside a student's profile, not
                        on the roster card. The student was rolled over into a future term you have not opened for
                        enrolment. Nothing is actionable until you open the term; the Confirm option appears then.
                    </p>

                    <p style={{ marginTop: "14px" }}><strong>Profile icons</strong></p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "6px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiPaw}
                                size={0.8}
                                color="var(--dl-color-purple-default)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong>Dance Localé member.</strong> This student has a full Dance Localé platform
                                account linked to their school record.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiAccountCircleOutline}
                                size={0.8}
                                color="var(--dl-color-purple-900)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong>School profile only.</strong> This student exists in your records but has
                                not yet created a Dance Localé account. You can send them an invite from their
                                profile.
                            </span>
                        </div>
                    </div>

                    <p style={{ marginTop: "14px" }}><strong>Payment icons</strong></p>
                    <p style={{ marginTop: "4px" }}>
                        The payment icon on each card summarises payment status across all current enrolments.
                        The same icon is used on the class Check-In screen, so it always means the same thing.
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardCheck}
                                size={0.8}
                                color="var(--dl-color-greentick)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-greentick)" }}>Green (solid).</strong> All
                                current enrolments are fully paid and there are no outstanding records elsewhere at
                                the school.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardSyncOutline}
                                size={0.8}
                                color="var(--dl-color-greentick)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-greentick)" }}>Green (sync).</strong> Paid
                                externally. At least one current enrolment was paid via cash, EFTPOS, bank transfer, or
                                another out-of-platform method, so it counts as paid. The sync icon just notes it has not
                                been reconciled yet. Reconcile it in Manage Finances against your bank/EFTPOS
                                transactions etc. This can be done whenever you balance your books.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardClockOutline}
                                size={0.8}
                                color="var(--dl-color-purple-900)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-purple-900)" }}>Purple.</strong> Payment
                                plan in progress. The student is on an instalment plan; at least one instalment is
                                paid and more are still expected.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardPlusOutline}
                                size={0.8}
                                color="var(--dl-color-blue-default)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-blue-default)" }}>Blue.</strong> Awaiting
                                price package. The enrolment is confirmed but you have not assigned a price package
                                yet, so no payment can be collected until you do.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardClockOutline}
                                size={0.8}
                                color="var(--dl-color-needs-action)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-needs-action)" }}>Orange.</strong> Payment
                                outstanding. The student has an unpaid balance on at least one current enrolment.
                            </span>
                        </div>
                    </div>
                    <p style={{ marginTop: "8px" }}>
                        Click the payment icon to open the payment modal and record or collect a payment.
                    </p>

                    <p style={{ marginTop: "14px" }}><strong>Card interactions</strong></p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "6px" }}>
                        <div>
                            <strong>Tap the card</strong> to open the student's full profile, where you can view class
                            history, edit their details, manage enrolments, enrol them in a new class, and send an
                            invite email.
                        </div>
                        <div>
                            <strong>Tap the avatar</strong> to add, change, or remove the student's photo without
                            opening their profile.
                        </div>
                        <div>
                            <strong>Tap the payment icon</strong> to open the payment modal and record or collect a
                            payment.
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiAccountPlusOutline}
                                size={0.8}
                                color="var(--dl-color-purple-900)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong>Enrol.</strong> On a Not Enrolled student's card, an Enrol button opens the
                                enrolment modal directly so you can place them in a class.
                            </span>
                        </div>
                    </div>

                    <p style={{ marginTop: "14px" }}><strong>Student photos</strong></p>
                    <p>
                        Each student card shows a circular avatar. If no photo has been uploaded, the student's
                        initials are shown as a placeholder.
                    </p>
                    <p style={{ marginTop: "6px" }}>
                        Click the avatar to add, change, or remove a photo. Photos uploaded here also appear on
                        the Check-In screen. If the student has a linked Dance Localé account, their platform
                        profile photo is used as a fallback; a school-uploaded photo always takes priority.
                    </p>

                    <p style={{ marginTop: "14px" }}><strong>Inviting students to join Dance Localé</strong></p>
                    <p>
                        Students added manually or imported via CSV are not linked to a Dance Localé account yet.
                        Your school records work fully without one. If a student wants to use the mobile app, send
                        them an invite email from their profile (Send Invite). The link opens a join page for your
                        school:
                    </p>
                    <ul>
                        <li>
                            <strong>New to Dance Localé:</strong> they enter their name, choose a password and tap
                            Create My Account.
                        </li>
                        <li>
                            <strong>Already have an account with that email:</strong> they enter their existing
                            password and tap Log in and join. Their current password stays the same.
                        </li>
                    </ul>
                    <p>Either way, their account is linked to their enrolment history at your school.</p>

                    <p style={{ marginTop: "14px" }}><strong>Removing a student</strong></p>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginTop: "6px" }}>
                        <Icon
                            path={mdiTrashCan}
                            size={0.8}
                            color="var(--dl-color-danger)"
                            style={{ flexShrink: 0, marginTop: "2px" }}
                        />
                        <span>
                            The red trash-can icon in a student's detail modal removes them from your school
                            entirely. This is different from the <strong>Remove from class</strong> icon on an
                            enrolment (which only ends that one enrolment) and from placing a student{" "}
                            <strong>On Break</strong> (which keeps their record and pauses them). Removing a
                            student deletes your school's record of them. If they have made payments, those
                            payment records are kept for tax and accounting with the student's identity removed
                            (shown as "Removed Student"); otherwise the record is deleted entirely. If the student
                            has their own Dance Localé account, only your school's link to them is removed, and
                            their account is untouched. This cannot be undone.
                        </span>
                    </div>

                    <p style={{ marginTop: "14px" }}><strong>Bulk import</strong></p>
                    <p>
                        If you have an existing student list, you can import it via the Bulk Import card in Manage
                        Classes. The import has two steps:
                    </p>
                    <p style={{ marginTop: "6px" }}>
                        <strong>Step 1: Map class codes (optional).</strong> If your spreadsheet uses shorthand
                        codes for classes (for example, <code>Z1</code> for Zouk Level 1), enter each code and
                        select the matching class from your current term. If your CSV has no class column, skip
                        this step.
                    </p>
                    <p style={{ marginTop: "6px" }}>
                        <strong>Step 2: Upload your CSV.</strong> Download the template, fill it in using any
                        spreadsheet app, and upload. The template has six columns:{" "}
                        <code>first_name, last_name, email, phone, class_code, payment_status</code>. The class
                        code and payment status columns are optional; leave them blank for a profile-only import.
                    </p>
                    <p style={{ marginTop: "6px" }}>
                        The preview table shows a colour-coded badge for each student: green means the class code
                        was matched, orange means it was not recognised (you can fix it inline using the dropdown
                        on each row). Students already in your roster are flagged and skipped automatically.
                    </p>
                </>
            ),
        },
        {
            id: "refunds",
            title: "4. Refunds: when and how money is returned",
            content: (
                <>
                    <p>
                        <strong>Refunds are off by default.</strong> Many schools simply remove a student who
                        leaves mid-term and return no money. If your school <em>does</em> issue cash refunds, turn
                        them on first:
                    </p>
                    <p style={{ marginTop: "8px" }}>
                        Go to <strong>Classes &rarr; Manage Classes / Setup &rarr; Class Pricing</strong>. At the top
                        of that page, under <strong>Transaction Settings</strong>, switch on{" "}
                        <strong>"This school offers class refunds"</strong>. Until this is on, no refund option
                        appears anywhere.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        <strong>How a refund is issued:</strong> there is no separate "refund" button. A refund is
                        offered at the moment you remove a student. Open a student, then on the enrolment you want to
                        end click the <strong>trash-can (Remove from class)</strong> icon. The Remove confirmation
                        appears, and &mdash; only when refunds are on &mdash; it includes an{" "}
                        <strong>"Issue Stripe refund"</strong> tick box and an amount field. Enter the amount to
                        refund (or untick to remove without a refund) and confirm.
                    </p>
                    <ul style={{ marginTop: "8px" }}>
                        <li>
                            <strong>Full refund</strong> (the whole amount) returns the money and{" "}
                            <strong>removes the student from the class</strong>.
                        </li>
                        <li>
                            <strong>Partial refund</strong> (less than the full amount) returns part of the money;
                            the student <strong>stays enrolled and still counts as paid</strong>.
                        </li>
                        <li>
                            The refund returns the class fee only &mdash; the card/processing service fee is never
                            refundable.
                        </li>
                    </ul>
                    <p style={{ marginTop: "10px" }}>
                        The same refund option also appears when you cancel a whole class or cancel a session, and is
                        gated by the same setting. Every refund is recorded in the <strong>History tab</strong> (see
                        below) with the amount and the date it happened.
                    </p>
                </>
            ),
        },
        {
            id: "attendance-tab",
            title: "5. Attendance tab",
            content: (
                <>
                    <p>
                        The Attendance tab lets you run an attendance report across your school. Filter by term,
                        class, or a custom date range, then click <strong>Load Report</strong> to generate the
                        results.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        Each row shows a student's name, the number of sessions attended, and their attendance
                        percentage for the selected period. The percentage badge is colour coded:
                    </p>
                    <ul style={{ marginTop: "6px" }}>
                        <li><strong>Green</strong> (75% or above): strong attendance</li>
                        <li><strong>Amber</strong> (40 to 74%): moderate attendance</li>
                        <li><strong>Red</strong> (below 40%): low attendance</li>
                    </ul>
                    <p style={{ marginTop: "10px" }}>
                        Click any row to see a full breakdown of which sessions the student attended or missed.
                        You can sort the list by name, sessions attended, or attendance percentage.
                    </p>
                </>
            ),
        },
        {
            id: "history-tab",
            title: "6. History tab",
            content: (
                <>
                    <p>
                        The History tab shows a school-wide record of past and cancelled enrolments. Use it to
                        look up a student's class history or review enrolments that were cancelled.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        Filter by year, term, class, or enrolment status to narrow the results. You can also
                        search by student name. Results are sortable by student name, class, term end date, or the
                        <strong> Action date</strong> (when a removal or refund happened).
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        The <strong>Status</strong> column tells you what happened to each enrolment:{" "}
                        <strong>Refunded</strong> (the student was removed and money was returned &mdash; the amount
                        is shown), <strong>Removed</strong> (removed with no refund), <strong>Past</strong> (the term
                        simply ended), or <strong>Inactive</strong>. The <strong>Action date</strong> column shows
                        when the removal/refund was actioned, separate from the class's scheduled time. Export to CSV
                        to keep a record.
                    </p>
                </>
            ),
        },
        {
            id: "payments-tab",
            title: "7. Payments tab",
            content: (
                <>
                    <p>
                        The Payments tab is a per-student payment history and balance view across <strong>every term</strong>.
                        By default it lists only students who have payment activity or an outstanding balance. Tick{" "}
                        <strong>Show settled (zero balance)</strong> to include students who are fully paid up, and use the
                        search box to find any student regardless of their balance.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        Click a student to expand their <strong>ledger</strong>. Each charge and payment appears as a single
                        line grouped under its term, with a per-term balance and an overall <strong>Balance owed</strong> at
                        the bottom. A paid line shows a green Paid pill with the method, date, and reference; an unpaid line
                        shows a <strong>Collect Payment</strong> action.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        Two square-up actions: <strong>Add charge</strong> creates a flat charge that isn't tied to a
                        class package (a balance carried over, a correction, a drop-in/walk-in one-off, or school
                        merchandise) and gives it an automatic reference code. In Finances and on the Reconcile page
                        this shows under the <strong>Adjustment</strong> type, which is simply the ledger category for
                        charges you add this way. <strong>Collect Payment</strong> on an outstanding item lets you mark it
                        paid, show a QR code, or email a payment link. A charge raised in error can be removed with the
                        trash icon while it is still unpaid.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        This tab replaces the old <strong>Pending $</strong> filter that used to sit on the Students tab: that
                        filter only ever looked at the current term, whereas the Payments tab tracks balances across all terms.
                    </p>
                </>
            ),
        },
        {
            id: "print-tab",
            title: "8. Print Class List tab",
            content: (
                <>
                    <p>
                        The Print Class List tab generates a landscape roll-call sheet you can print and bring to
                        class. Choose whether you want a regular term class list or a short course list, then
                        select the term or course.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        You can filter the list further by dance style, day of the week, or role (Leaders,
                        Followers, or both). The preview updates as you change the filters.
                    </p>
                    <p style={{ marginTop: "10px" }}>
                        Each class block in the preview shows enrolled students split into Leaders and Followers
                        columns. Confirmed enrolments are displayed normally; unconfirmed enrolments are shown
                        with a visual indicator. When you are happy with the preview, click{" "}
                        <strong>Print</strong> to send it to your printer.
                    </p>
                </>
            ),
        },
    ];

    return (
        <div className="class-settings-help-overlay" onClick={onClose}>
            <div className="class-settings-help-modal" onClick={e => e.stopPropagation()}>
                <div className="class-settings-help-header">
                    <div className="class-settings-help-header-content">
                        <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
                        <h2>Manage Students Help</h2>
                    </div>
                    <button className="class-settings-help-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="class-settings-help-body">
                    {sections.map(section => (
                        <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
                            <button className="help-accordion-header" onClick={() => toggleSection(section.id)}>
                                <Icon
                                    path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                                    size={0.8}
                                    color="var(--dl-color-purple-default)"
                                />
                                <span>{section.title}</span>
                            </button>
                            {expandedSections.includes(section.id) && (
                                <div className="help-accordion-content">{section.content}</div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="class-settings-help-footer">
                    <button className="classes-help-btn-secondary" onClick={onClose}>
                        Got it!
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClassStudentsHelpModal;
